import React, { useRef } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DescribedRadioGroup } from "component/shared/described-radio-group";
import { FormSelectField } from "component/shared/form-fields";
import {
	TextMessageBusinessType,
	TextMessageTaxIdType,
	textMessageBusinessTypes,
	textMessageTaxIdTypes,
} from "type/text-message-tax-id";
import { styles } from "./style";

interface OwnProps {
	hasTaxId: boolean;
	onHasTaxIdChange: (hasTaxId: boolean) => void;
	/** The wizard describes each answer; the Texting page doesn't. */
	described?: boolean;
	complianceNote: string;
	taxIdType: TextMessageTaxIdType;
	onTaxIdTypeChange: (type: TextMessageTaxIdType) => void;
	/** The number field, which the wizard and the page render differently (the page masks it). */
	taxIdField: React.ReactNode;
	businessType: TextMessageBusinessType | "";
	onBusinessTypeChange: (type: TextMessageBusinessType) => void;
	businessTypeError?: string;
	/** Shown under Business Type in place of `businessTypeError`'s standard helper text, e.g. the wizard's FieldHelper. */
	businessTypeHelper?: React.ReactNode;
	/** The legal name field, and anything after it, e.g. the wizard's Mobile Phone Number. */
	children?: React.ReactNode;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

export const taxIdLabel = (type: TextMessageTaxIdType) => (type === "CBN" ? "Canadian BN" : "EIN");

/** The Business Tax ID question, shared by the setup wizard and the Texting page. */
const Component: React.FC<Props> = ({
	classes = {},
	hasTaxId,
	onHasTaxIdChange,
	described = false,
	complianceNote,
	taxIdType,
	onTaxIdTypeChange,
	taxIdField,
	businessType,
	onBusinessTypeChange,
	businessTypeError,
	businessTypeHelper,
	children,
}) => {
	const name = useRef(`tax-id-${Math.random().toString(36).slice(2)}`).current;
	const fieldClass = described ? classes.describedField : undefined;
	const fields = (
		<div className={described ? classes.describedFields : classes.fields}>
			{described && <Mui.Typography className={classes.note}>{complianceNote}</Mui.Typography>}
			<div className={described ? classes.describedType : undefined}>
				<Mui.Typography
					id={`${name}-type-label`}
					className={[classes.label, described && classes.describedLabel].filter(Boolean).join(" ")}
				>
					ID Type<span className={classes.asterisk}>*</span>
				</Mui.Typography>
				<DescribedRadioGroup
					name={`${name}-type`}
					value={taxIdType}
					options={textMessageTaxIdTypes.map((type) => ({ value: type.id, label: type.label }))}
					onChange={(value) => onTaxIdTypeChange(value as TextMessageTaxIdType)}
					aria-labelledby={`${name}-type-label`}
					classes={{ title: classes.plainTitle, option: classes.typeOption }}
				/>
			</div>
			{described ? taxIdField : <div className={fieldClass}>{taxIdField}</div>}
			<div className={businessTypeHelper ? classes.withHelper : fieldClass}>
				<div className={businessTypeHelper ? fieldClass : undefined}>
					<FormSelectField
						label="Business Type"
						required
						value={businessType}
						onChange={onBusinessTypeChange}
						options={textMessageBusinessTypes}
						valueExtractor={(option) => option.id}
						labelExtractor={(option) => option.label}
						error={!!businessTypeError}
						helperText={businessTypeHelper ? undefined : businessTypeError}
					/>
				</div>
				{businessTypeHelper}
			</div>
			{children}
		</div>
	);
	const yesFields = described ? fields : (
		<div className={classes.pageYes}>
			<Mui.Typography className={[classes.note, classes.pageNote].join(" ")}>{complianceNote}</Mui.Typography>
			{fields}
		</div>
	);
	return (
		<DescribedRadioGroup
			name={name}
			value={hasTaxId ? "yes" : "no"}
			options={[
				{
					value: "no",
					label: "No, my business does not have a tax ID",
					// Figma adds the mobile verification note only while No is the answer.
					description: described
						? `This is the most common choice for real estate agents operating as sole proprietors.${hasTaxId ? "" : " Mobile verification required."}`
						: undefined,
				},
				{
					value: "yes",
					label: "Yes, my business has a tax ID",
					description: described ? "We'll ask for your EIN (XX-XXXXXXX) or BN (9 digits) and legal business name." : undefined,
					after: hasTaxId ? yesFields : undefined,
				},
			]}
			onChange={(value) => onHasTaxIdChange(value === "yes")}
			classes={
				described
					? { option: classes.describedOption, description: classes.describedDescription, after: classes.describedAfter }
					: { title: classes.plainTitle, option: classes.compactOption }
			}
		/>
	);
};

export const TaxIdFields = withStyles(Component, styles);
